// components/CommonSelect.tsx

import React from "react";
import {
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  Autocomplete,
  createFilterOptions,
} from "@mui/material";
import { SxProps, Theme } from "@mui/material/styles";

export interface MenuOption {
  label: string;
  value: string;
}

export interface OptionGroup {
  header?: string;
  options: MenuOption[];
}

interface Props {
  value?: string;
  placeholder: string;
  options?: MenuOption[];
  groupedOptions?: OptionGroup[];
  onChangeHandler: (v: string) => void;
  disabled?: boolean;
  showSectionHeader?: boolean;
  scrollContainerId?: string;
  searchable?: boolean;
  loading?: boolean;
  sx?: SxProps<Theme>;
  borderColor?: string;
}

const CommonSelect: React.FC<Props> = ({
  value = "",
  placeholder,
  options = [],
  groupedOptions = [],
  onChangeHandler,
  disabled,
  showSectionHeader = false,
  searchable = false,
  loading = false,
  sx = {},
  borderColor = "#000",
}) => {
  const onOptionChange = (value: string) => {
    onChangeHandler(value);
  };

  const commonStyles = {
    "& .MuiOutlinedInput-root": {
      height: 56,
      backgroundColor: "#fff",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    ...sx,
  };

  // SEARCHABLE (AUTOCOMPLETE)
  if (searchable) {
    const isGroupedSearchable = groupedOptions && groupedOptions.length > 0;
    const flatOptions = isGroupedSearchable
      ? groupedOptions.flatMap((group) =>
          group.options.map((o) => ({ ...o, group: group.header ?? "" }))
        )
      : options.map((o) => ({ ...o, group: "" }));

    const defaultFilter = createFilterOptions<(typeof flatOptions)[number]>();

    // Collapse repeated whitespace so "Event   Test" and "Event Test" are
    // treated the same continuous phrase.
    const normalize = (text: string) =>
      text.trim().toLowerCase().replace(/\s+/g, " ");

    // Once the user types more than 3 characters, only keep events whose
    // name contains the typed phrase as a continuous substring; everything
    // else is removed from the list. Clearing the input (<= 3 chars)
    // restores the full, unfiltered list.
    const searchFilter = (
      opts: typeof flatOptions,
      state: Parameters<typeof defaultFilter>[1],
    ) => {
      const input = normalize(state.inputValue);

      if (input.length <= 3) {
        return opts;
      }

      return opts.filter((o) => normalize(o.label).includes(input));
    };

    return (
      <Autocomplete
        fullWidth
        disabled={disabled}
        loading={loading}
        options={flatOptions}
        filterOptions={searchFilter}
        groupBy={isGroupedSearchable ? (option) => option.group : undefined}
        slotProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-option": {
                fontSize: "17px",
              },
              "& .MuiAutocomplete-groupLabel": {
                backgroundColor: "#f9f7f7",
                color: "#1a1a1a",
                fontWeight: 700,
                fontSize: "13px",
              },
            },
          },
        }}
        value={flatOptions.find((o) => o.value === value) || null}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.value === val.value}
        onChange={(_, newValue) => {
          onOptionChange(newValue?.value || "");
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} />
        )}
        sx={commonStyles}
      />
    );
  }

  // GROUPED LIST
  const isGrouped =
    showSectionHeader && groupedOptions && groupedOptions.length > 0;

  return (
    <Select
      fullWidth
      displayEmpty
      value={value}
      disabled={disabled}
      variant="outlined"
      onChange={(e) => onOptionChange(e.target.value as string)}
      MenuProps={{
        disableScrollLock: true,
        PaperProps: {
          style: {
            maxHeight: 200,
            maxWidth: 140,
          },
        },
      }}
      sx={{
        height: 56,
        backgroundColor: "#fff",
        ...sx,
      }}
    >
      <MenuItem value="" disabled>
        {placeholder}
      </MenuItem>

      {isGrouped
        ? groupedOptions.map((group, gIndex) => [
            group.header && (
              <ListSubheader
                key={`header-${group.header}-${gIndex}`}
                sx={{
                  backgroundColor: "#f9f7f7",
                  color: "#1a1a1a",
                  fontWeight: 700,
                  lineHeight: "36px",
                  fontSize: "13px",
                  borderBottom: "1px solid #faf6f6",
                  zIndex: 1,
                }}
              >
                {group.header}
              </ListSubheader>
            ),

            group.options.map((o, index) => (
              <MenuItem
                key={`group-item-${o.value}-${index}`}
                sx={{
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                  minHeight: "unset",
                  py: 1,
                  fontSize: "14px",
                }}
                value={o.value}
              >
                {o.label}
              </MenuItem>
            )),
          ])
        : options.map((o, index) => (
            <MenuItem
              key={`item-${o.value}-${index}`}
              sx={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.4,
                minHeight: "unset",
                py: 1,
                fontSize: "14px",
              }}
              value={o.value}
            >
              {o.label}
            </MenuItem>
          ))}
    </Select>
  );
};

export default CommonSelect;
